<!-- src/components/Legal/LegalDocsModal.vue -->
<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";

const props = defineProps({
  defaultDoc: {
    type: String,
    default: "terms",
  },
});

const emit = defineEmits(["close"]);

const activeDoc = ref(props.defaultDoc);

const docs = [
  { id: "terms",        icon: "fas fa-file-contract",   label: "Termos de Uso" },
  { id: "privacy",      icon: "fas fa-shield-alt",       label: "Política de Privacidade" },
  { id: "subscriptions",icon: "fas fa-credit-card",      label: "Assinaturas e Pagamentos" },
  { id: "acceptable",   icon: "fas fa-check-circle",     label: "Uso Aceitável" },
  { id: "copyright",    icon: "fas fa-copyright",        label: "Direitos Autorais" },
];

function navigateTo(docId) {
  activeDoc.value = docId;
  // scroll ao topo do conteudo
  const el = document.getElementById("legal-content-area");
  if (el) el.scrollTop = 0;
}

function closeModal() {
  emit("close");
}

function handleEscKey(event) {
  if (event.key === "Escape") closeModal();
}

onMounted(() => document.addEventListener("keydown", handleEscKey));
onUnmounted(() => document.removeEventListener("keydown", handleEscKey));

// ─────────────────────────────────────────────────────────────
// Conteudo dos documentos
// Cada documento e um array de secoes: { type, text, level }
// type: 'intro' | 'h1' | 'h2' | 'body' | 'item'
// ─────────────────────────────────────────────────────────────

const documents = {

  // ══════════════════════════════════════════════════════════
  // 1. TERMOS DE USO (v2)
  // ══════════════════════════════════════════════════════════
  terms: {
    title: "Termos de Uso",
    version: "Versão 1.0 — Vigência a partir de 30 de março de 2025",
    sections: [
      { type: "h1", text: "Introdução" },
      { type: "body", text: 'Bem-vindo ao <b>Litterae</b>. Estes Termos de Uso (<b>"Termos"</b>) regem o acesso e a utilização da plataforma Litterae, seus conteúdos, funcionalidades e serviços disponibilizados por meio de interface web (<b>"Plataforma"</b>). Ao criar uma conta ou acessar a Plataforma de qualquer forma, você (<b>"Usuário"</b>) declara ter lido, compreendido e concordado integralmente com estes Termos e com todos os documentos a eles incorporados por referência.' },
      { type: "body", text: "Caso não concorde com qualquer disposição destes Termos, você não está autorizado a utilizar a Plataforma e deve descontinuar imediatamente o seu uso." },
      { type: "body", text: 'Estes Termos complementam, mas não substituem, os seguintes documentos que integram o ecossistema jurídico do Litterae: (i) <a data-doc="privacy">Política de Privacidade</a>; (ii) <a data-doc="subscriptions">Política de Assinaturas e Pagamentos</a>; (iii) <a data-doc="acceptable">Política de Uso Aceitável</a>; e (iv) <a data-doc="copyright">Política de Direitos Autorais</a>.' },

      { type: "h1", text: "1. Definições" },
      { type: "body", text: "Para os fins destes Termos, os seguintes termos têm o significado abaixo atribuído:" },
      { type: "item", text: "<b>Litterae ou Plataforma:</b> o ecossistema digital de gerenciamento de assinaturas e conteúdos literários, acessível por meio de interface web." },
      { type: "item", text: "<b>Usuário:</b> toda pessoa física ou jurídica que cria uma conta e utiliza a Plataforma, seja no plano gratuito ou em qualquer plano pago." },
      { type: "item", text: "<b>Conta:</b> o registro individual do Usuário na Plataforma, protegido por credenciais de acesso." },
      { type: "item", text: "<b>Conteúdo:</b> textos literários, resenhas, obras, anotações e quaisquer outros materiais disponibilizados na Plataforma, seja pelo Litterae, por seus parceiros ou pelos próprios Usuários." },
      { type: "item", text: '<b>Plano:</b> o conjunto de funcionalidades e limites de acesso vinculados a uma modalidade de assinatura, conforme descrito na <a data-doc="subscriptions">Política de Assinaturas e Pagamentos</a>.' },
      { type: "item", text: "<b>Assinatura:</b> o vínculo contratual entre o Usuário e o Litterae que confere acesso a um determinado Plano mediante pagamento periódico ou gratuito." },

      { type: "h1", text: "2. Elegibilidade e Cadastro" },
      { type: "body", text: "Para criar/usar uma Conta no Litterae, o Usuário deve: (i) ter capacidade civil plena, nos termos da legislação brasileira; (ii) ser maior de 18 (dezoito) anos ou, se menor, possuir autorização expressa do responsável legal; e (iii) fornecer informações verídicas, precisas e atualizadas no momento do cadastro." },
      { type: "body", text: "O Usuário é o único responsável pela veracidade dos dados cadastrais informados e pelo sigilo de suas credenciais de acesso. O Litterae não se responsabiliza por acessos não autorizados decorrentes de negligência do Usuário na guarda de sua senha." },
      { type: "body", text: "É vedado o cadastro de robôs, scripts ou qualquer mecanismo automatizado para criação de Contas. O Litterae reserva-se o direito de recusar, suspender ou encerrar cadastros que violem esta disposição, sem aviso prévio." },

      { type: "h1", text: "3. Acesso e Uso da Plataforma" },
      { type: "body", text: "O Litterae concede ao Usuário uma licença limitada, pessoal, intransferível, não exclusiva e revogável para acessar e utilizar a Plataforma de acordo com estes Termos. Essa licença não transfere qualquer direito de propriedade sobre a Plataforma ou seus Conteúdos." },
      { type: "body", text: 'O acesso às funcionalidades da Plataforma está condicionado ao plano de assinatura ativo do Usuário, conforme detalhado na <a data-doc="subscriptions">Política de Assinaturas e Pagamentos</a>. O Litterae pode, a qualquer momento, modificar, suspender ou descontinuar funcionalidades, mediante comunicação prévia ao Usuário quando tal alteração for relevante.' },
      { type: "body", text: 'O uso indevido da Plataforma, em desacordo com a <a data-doc="acceptable">Política de Uso Aceitável</a>, poderá resultar na suspensão ou encerramento da Conta, sem prejuízo das demais medidas cabíveis.' },

      { type: "h1", text: "4. Contas e Segurança" },
      { type: "body", text: "Cada Usuário pode manter apenas uma Conta ativa na Plataforma. A transferência, cessão ou compartilhamento de Conta é expressamente proibida." },
      { type: "body", text: "O Usuário deve notificar imediatamente o Litterae, por meio dos canais de suporte disponíveis, caso suspeite de acesso não autorizado à sua Conta ou de qualquer violação de segurança. O Litterae tomará as medidas razoáveis para investigar e mitigar o incidente." },
      { type: "body", text: "O Litterae poderá, a seu exclusivo critério e sem aviso prévio, suspender o acesso de uma Conta quando houver indícios de comprometimento de segurança, atividade fraudulenta ou violação destes Termos, devendo notificar o Usuário tão logo quanto possível após a medida." },

      { type: "h1", text: "5. Assinaturas e Pagamentos" },
      { type: "body", text: 'As condições detalhadas relativas a planos, preços, métodos de pagamento, renovação automática, upgrade, cancelamento e rebaixamento de plano estão integralmente descritas na <a data-doc="subscriptions"><b>Política de Assinaturas e Pagamentos</b></a>, que é parte integrante destes Termos.' },
      { type: "body", text: "O Litterae reserva-se o direito de alterar os valores dos planos, mediante notificação ao Usuário com antecedência mínima de 30 (trinta) dias. A continuidade do uso da Plataforma após a entrada em vigor dos novos valores implica a aceitação das condições alteradas." },

      { type: "h1", text: "6. Conteúdo do Usuário" },
      { type: "body", text: "O Usuário permanece titular de todo o Conteúdo que publicar na Plataforma. Ao publicar Conteúdo, o Usuário concede ao Litterae uma licença não exclusiva, gratuita, mundial e pelo prazo necessário para a prestação dos serviços, para hospedar, exibir, reproduzir e distribuir tal Conteúdo exclusivamente no âmbito da Plataforma." },
      { type: "body", text: 'O Usuário declara e garante que: (i) detém todos os direitos necessários sobre o Conteúdo publicado; (ii) o Conteúdo não viola direitos de terceiros, incluindo direitos autorais, marcas registradas, privacidade e imagem; e (iii) o Conteúdo está em conformidade com a <a data-doc="acceptable">Política de Uso Aceitável</a> e a <a data-doc="copyright">Política de Direitos Autorais</a>.' },
      { type: "body", text: "O Litterae não se responsabiliza pelo Conteúdo publicado pelos Usuários, reservando-se o direito de remover, sem aviso prévio, qualquer Conteúdo que viole estes Termos ou a legislação aplicável." },

      { type: "h1", text: "7. Propriedade Intelectual" },
      { type: "body", text: "A Plataforma, incluindo seu código-fonte, design, logotipos, marcas, textos institucionais, funcionalidades e demais elementos desenvolvidos pelo Litterae, são protegidos pelas leis brasileiras e internacionais de propriedade intelectual e pertencem exclusivamente ao Litterae ou a seus licenciantes." },
      { type: "body", text: "É vedado ao Usuário: (i) reproduzir, copiar, distribuir ou explorar comercialmente qualquer elemento da Plataforma sem autorização prévia e por escrito; (ii) realizar engenharia reversa, descompilar ou tentar extrair o código-fonte da Plataforma; ou (iii) remover ou alterar avisos de propriedade intelectual." },
      { type: "body", text: 'As regras específicas sobre direitos autorais dos Conteúdos disponibilizados na Plataforma estão detalhadas na <a data-doc="copyright"><b>Política de Direitos Autorais</b></a>.' },

      { type: "h1", text: "8. Privacidade e Proteção de Dados" },
      { type: "body", text: 'O tratamento de dados pessoais do Usuário é realizado em conformidade com a Lei Geral de Proteção de Dados Pessoais (LGPD – Lei nº 13.709/2018) e está integralmente descrito na <a data-doc="privacy"><b>Política de Privacidade</b></a>, que o Usuário declara ter lido e aceito ao criar sua Conta.' },
      { type: "body", text: "O Litterae adota medidas técnicas e organizacionais adequadas para proteger os dados pessoais dos Usuários contra acesso não autorizado, perda, destruição ou divulgação indevida." },

      { type: "h1", text: "9. Limitação de Responsabilidade" },
      { type: "body", text: 'A Plataforma é fornecida <i>"no estado em que se encontra"</i>. O Litterae não garante que a Plataforma estará disponível de forma ininterrupta, livre de erros ou totalmente segura, podendo realizar manutenções programadas ou emergenciais.' },
      { type: "body", text: "Na máxima extensão permitida pela legislação aplicável, o Litterae não será responsável por: (i) danos indiretos, incidentais, especiais ou consequenciais; (ii) perda de dados ou lucros cessantes decorrentes do uso ou da impossibilidade de uso da Plataforma; ou (iii) atos de terceiros, incluindo ataques cibernéticos ou falhas de provedores externos." },
      { type: "body", text: "Em nenhuma hipótese a responsabilidade total do Litterae perante o Usuário, por qualquer causa, excederá o valor pago pelo Usuário nos 12 (doze) meses anteriores ao evento que originou o dano." },

      { type: "h1", text: "10. Prazo e Rescisão" },
      { type: "body", text: "Estes Termos vigoram por prazo indeterminado, a partir do momento em que o Usuário cria sua Conta ou acessa a Plataforma pela primeira vez." },
      { type: "body", text: "O Usuário pode encerrar sua Conta a qualquer momento, diretamente pela Plataforma em <b>Perfil &gt; Resumo da Conta &gt; Excluir Conta</b>, ou mediante solicitação pelos canais de suporte. O encerramento não implica reembolso de valores já pagos, exceto quando expressamente previsto na Política de Assinaturas e Pagamentos." },
      { type: "body", text: "O Litterae pode encerrar ou suspender a Conta do Usuário, a qualquer momento e sem aviso prévio, em caso de: (i) violação destes Termos ou de qualquer política vinculada; (ii) atividade fraudulenta ou ilegal; (iii) não pagamento de valores devidos; ou (iv) determinação judicial ou administrativa." },
      { type: "body", text: "Em caso de encerramento por iniciativa do Litterae sem justa causa, o Usuário receberá reembolso proporcional ao período não usufruído de sua assinatura paga vigente." },

      { type: "h1", text: "11. Modificações dos Termos" },
      { type: "body", text: "O Litterae pode modificar estes Termos a qualquer momento. Alterações relevantes serão comunicadas ao Usuário com antecedência mínima de 15 (quinze) dias, por e-mail ou por notificação dentro da Plataforma. Alterações meramente formais ou gramaticais podem ser realizadas sem comunicação prévia." },
      { type: "body", text: "O uso continuado da Plataforma após a entrada em vigor das alterações implica a aceitação das novas condições. Caso o Usuário não concorde com as modificações, poderá encerrar sua Conta antes da data de vigência, sem penalidade." },

      { type: "h1", text: "12. Disposições Gerais" },
      { type: "item", text: "<b>Lei aplicável:</b> Estes Termos são regidos pelas leis da República Federativa do Brasil." },
      { type: "item", text: "<b>Foro:</b> Fica eleito o foro da comarca de Maceió, Estado de Alagoas, para dirimir quaisquer controvérsias decorrentes destes Termos, com renúncia expressa a qualquer outro, por mais privilegiado que seja." },
      { type: "item", text: "<b>Independência das cláusulas:</b> A nulidade ou inaplicabilidade de qualquer cláusula destes Termos não afeta a validade das demais, que permanecerão em pleno vigor." },
      { type: "item", text: "<b>Integralidade:</b> Estes Termos, em conjunto com os documentos neles referenciados, constituem o acordo integral entre o Usuário e o Litterae, prevalecendo sobre quaisquer entendimentos anteriores sobre o mesmo objeto." },
      { type: "item", text: "<b>Ausência de renúncia:</b> A omissão do Litterae em exigir o cumprimento de qualquer disposição destes Termos não constituirá renúncia ao direito de fazê-lo no futuro." },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // 2. POLÍTICA DE PRIVACIDADE (v2)
  // ══════════════════════════════════════════════════════════
  privacy: {
    title: "Política de Privacidade",
    version: "Versão 1.0 — Vigência a partir de 30 de março de 2025",
    sections: [
      { type: "h1", text: "Introdução" },
      { type: "body", text: 'A presente Política de Privacidade descreve como o <b>Litterae</b> coleta, utiliza, armazena, compartilha e protege os dados pessoais dos Usuários da Plataforma, em conformidade com a <b>Lei Geral de Proteção de Dados Pessoais (LGPD – Lei nº 13.709/2018)</b> e demais normas aplicáveis.' },
      { type: "body", text: "Ao criar uma Conta ou utilizar a Plataforma, o Usuário declara ter lido e concordado com esta Política. Caso não concorde, deve descontinuar o uso imediatamente." },
      { type: "body", text: 'Esta Política integra os <a data-doc="terms">Termos de Uso</a> do Litterae e deve ser lida em conjunto com os demais documentos do ecossistema jurídico da Plataforma.' },

      { type: "h1", text: "1. Controlador dos Dados" },
      { type: "body", text: "O controlador dos dados pessoais tratados no âmbito desta Política é o <b>Litterae</b>, operador da plataforma digital de gerenciamento de assinaturas e conteúdos literários, acessível por meio de interface web." },
      { type: "body", text: "Para questões relacionadas ao tratamento de dados pessoais, o Usuário pode entrar em contato diretamente pela Plataforma, através do botão <b>Fale Conosco &gt; Suporte</b>." },

      { type: "h1", text: "2. Dados Pessoais Coletados" },
      { type: "body", text: "O Litterae coleta dados pessoais nas seguintes categorias:" },

      { type: "h2", text: "2.1 Dados fornecidos diretamente pelo Usuário" },
      { type: "body", text: "No momento do cadastro e durante o uso da Plataforma, o Usuário fornece: nome completo; endereço de e-mail; senha (armazenada em formato criptografado); e dados de pessoa jurídica, quando aplicável (razão social, CNPJ)." },

      { type: "h2", text: "2.2 Dados de pagamento" },
      { type: "body", text: "Para processar pagamentos via PIX ou cartão de crédito, o Litterae coleta os dados necessários para criação de preferências de pagamento junto ao <b>Mercado Pago</b>, processador financeiro integrado à Plataforma. Isso inclui: e-mail do pagador; token do cartão gerado pelo Brick do Mercado Pago (nunca o número completo do cartão); e identificador do método de pagamento (ex.: visa, mastercard)." },
      { type: "body", text: "O Litterae não armazena dados completos de cartão de crédito em seus servidores. O processamento e tokenização são realizados diretamente pelo Mercado Pago, sujeito à sua própria política de privacidade." },

      { type: "h2", text: "2.3 Dados de uso e navegação" },
      { type: "body", text: "Durante a navegação na Plataforma, podem ser coletados automaticamente: endereço IP; tipo e versão do navegador; páginas acessadas e tempo de permanência; data e hora de acesso; e identificadores de sessão." },

      { type: "h2", text: "2.4 Dados transacionais e de assinatura" },
      { type: "body", text: 'O Litterae registra os dados relativos ao histórico de assinaturas do Usuário, incluindo: planos contratados e seus ciclos de cobrança; método de pagamento utilizado; status de pagamento e renovações; cupons de desconto aplicados; e histórico de upgrades e rebaixamentos de plano, conforme detalhado na <a data-doc="subscriptions">Política de Assinaturas e Pagamentos</a>.' },

      { type: "h1", text: "3. Finalidades do Tratamento" },
      { type: "body", text: "Os dados pessoais coletados são tratados para as seguintes finalidades, com suas respectivas bases legais nos termos da LGPD:" },

      { type: "h2", text: "3.1 Execução do contrato (art. 7º, V, LGPD)" },
      { type: "body", text: "Criação e gerenciamento da Conta do Usuário; processamento de pagamentos e gestão de assinaturas; aplicação de cupons de desconto; cálculo de crédito proporcional em upgrades de plano; controle de renovações automáticas e tratamento de falhas de pagamento; e rebaixamento automático para o plano gratuito em caso de expiração ou cancelamento." },

      { type: "h2", text: "3.2 Cumprimento de obrigação legal (art. 7º, II, LGPD)" },
      { type: "body", text: "Emissão de notas fiscais de serviço (NFS-e) e cumprimento de obrigações tributárias; atendimento a requisições de autoridades judiciais ou administrativas competentes; e retenção de registros financeiros pelo prazo legal exigido." },

      { type: "h2", text: "3.3 Legítimo interesse (art. 7º, IX, LGPD)" },
      { type: "body", text: "Prevenção a fraudes e atividades ilícitas na Plataforma; monitoramento de segurança e integridade do sistema; melhoria contínua dos serviços e funcionalidades; e envio de comunicações sobre atualizações relevantes do serviço." },

      { type: "h2", text: "3.4 Consentimento (art. 7º, I, LGPD)" },
      { type: "body", text: "Envio de comunicações de marketing, novidades e promoções, quando o Usuário optar por recebê-las. O consentimento pode ser revogado a qualquer momento pelo próprio Usuário diretamente pela Plataforma em <b>Perfil &gt; Demais dados &gt; Marketing</b>." },

      { type: "h1", text: "4. Compartilhamento de Dados" },
      { type: "body", text: "O Litterae não vende nem aluga dados pessoais de Usuários a terceiros. O compartilhamento ocorre apenas nas seguintes situações e com as garantias adequadas:" },
      { type: "item", text: "<b>Mercado Pago:</b> processador de pagamentos responsável pela execução de transações via PIX e cartão de crédito, conforme necessário para a prestação do serviço contratado." },
      { type: "item", text: "<b>Prestadores de serviço de infraestrutura:</b> provedores de hospedagem, banco de dados e serviços de e-mail transacional, vinculados por obrigações contratuais de confidencialidade e proteção de dados." },
      { type: "item", text: "<b>Autoridades públicas:</b> quando exigido por lei, ordem judicial ou regulatória, limitando-se ao mínimo necessário ao cumprimento da obrigação." },
      { type: "item", text: "<b>Sucessores corporativos:</b> em caso de fusão, aquisição ou transferência de ativos, com notificação prévia ao Usuário e manutenção das proteções desta Política." },

      { type: "h1", text: "5. Retenção de Dados" },
      { type: "body", text: "Os dados pessoais são retidos pelo Litterae pelo tempo necessário ao cumprimento das finalidades descritas nesta Política, observados os seguintes critérios:" },
      { type: "item", text: "<b>Dados de Conta ativa:</b> mantidos enquanto a Conta permanecer ativa na Plataforma." },
      { type: "item", text: "<b>Dados após encerramento da Conta:</b> retidos pelo prazo de 5 (cinco) anos para fins de cumprimento de obrigações legais, tributárias e para defesa em eventuais disputas." },
      { type: "item", text: "<b>Dados de pagamento e transacionais:</b> retidos pelo prazo mínimo de 5 (cinco) anos, conforme exigência fiscal e contábil aplicável." },
      { type: "item", text: "<b>Logs de acesso:</b> retidos por 6 (seis) meses, nos termos do Marco Civil da Internet (Lei nº 12.965/2014)." },
      { type: "body", text: "Após o encerramento dos prazos de retenção, os dados serão excluídos de forma segura ou anonimizados, de modo que não seja possível a identificação do titular." },

      { type: "h1", text: "6. Segurança dos Dados" },
      { type: "body", text: "O Litterae adota medidas técnicas e organizacionais adequadas para proteger os dados pessoais dos Usuários contra acesso não autorizado, perda, destruição, alteração ou divulgação indevida, incluindo:" },
      { type: "body", text: "Criptografia de senhas com algoritmos de hash seguro; comunicação via HTTPS/TLS; controle de acesso baseado em funções para dados sensíveis; tokenização de dados de cartão pelo Mercado Pago; e monitoramento de logs para detecção de atividades suspeitas." },
      { type: "body", text: "Em caso de incidente de segurança que possa acarretar risco ou dano relevante ao Usuário, o Litterae notificará a Autoridade Nacional de Proteção de Dados (ANPD) e os titulares afetados, nos prazos e formas exigidos pela LGPD." },

      { type: "h1", text: "7. Direitos do Titular dos Dados" },
      { type: "body", text: "Nos termos da LGPD, o Usuário, na qualidade de titular de dados pessoais, tem os seguintes direitos, exercíveis mediante solicitação ao Litterae:" },
      { type: "item", text: "<b>Confirmação e acesso:</b> saber se o Litterae trata seus dados e obter cópia das informações tratadas." },
      { type: "item", text: "<b>Correção:</b> solicitar a correção de dados incompletos, inexatos ou desatualizados." },
      { type: "item", text: "<b>Anonimização, bloqueio ou eliminação:</b> dos dados desnecessários, excessivos ou tratados em desconformidade com a LGPD." },
      { type: "item", text: "<b>Eliminação:</b> dos dados tratados com base no consentimento, ressalvadas as hipóteses de retenção legal." },
      { type: "item", text: "<b>Informação sobre compartilhamento:</b> saber com quais entidades públicas ou privadas o Litterae compartilhou seus dados." },
      { type: "item", text: "<b>Revogação do consentimento:</b> retirar o consentimento a qualquer momento, sem prejuízo da licitude do tratamento realizado anteriormente." },
      { type: "item", text: "<b>Oposição:</b> opor-se ao tratamento realizado com fundamento em hipóteses diversas do consentimento, em caso de descumprimento da LGPD." },
      { type: "body", text: "As solicitações devem ser encaminhadas diretamente pela Plataforma, através do botão <b>Fale Conosco &gt; Suporte</b>. O Litterae responde em até 15 (quinze) dias úteis, podendo solicitar confirmação de identidade." },

      { type: "h1", text: "8. Cookies e Tecnologias de Rastreamento" },
      { type: "body", text: "O Litterae utiliza cookies ou tecnologias similares para: manter a sessão autenticada do Usuário; lembrar preferências de configuração; e coletar métricas anônimas de uso da Plataforma para melhoria do serviço." },
      { type: "body", text: "O Usuário pode configurar seu navegador para recusar cookies, mas isso pode afetar o funcionamento de algumas funcionalidades da Plataforma. Não são utilizados cookies de rastreamento publicitário ou de terceiros para fins de anúncio." },

      { type: "h1", text: "9. Transferência Internacional de Dados" },
      { type: "body", text: "Os dados pessoais dos Usuários podem ser transferidos e armazenados em servidores localizados fora do Brasil, em razão da infraestrutura de nuvem utilizada pelo Litterae. Nestes casos, o Litterae assegura que a transferência ocorra em conformidade com a LGPD, mediante adoção de salvaguardas adequadas, como cláusulas contratuais padrão." },

      { type: "h1", text: "10. Dados de Menores de Idade" },
      { type: "body", text: "A Plataforma não é destinada a menores de 18 (dezoito) anos sem autorização do responsável legal. O Litterae não coleta intencionalmente dados de menores sem tal autorização. Caso identifique que dados de um menor foram coletados sem consentimento do responsável, providenciará a exclusão imediata." },

      { type: "h1", text: "11. Alterações desta Política" },
      { type: "body", text: "O Litterae pode atualizar esta Política a qualquer momento. Alterações relevantes serão comunicadas ao Usuário com antecedência mínima de 15 (quinze) dias, por e-mail ou notificação na Plataforma. A continuidade do uso após a entrada em vigor das alterações implica aceitação das novas condições." },

      { type: "h1", text: "12. Contato e Encarregado de Dados (DPO)" },
      { type: "body", text: "Para exercer seus direitos, esclarecer dúvidas ou registrar reclamações relacionadas ao tratamento de dados pessoais, o Usuário pode contatar diretamente o Litterae através do botão <b>Fale Conosco &gt; Suporte</b>, diretamente pela Plataforma." },
      { type: "body", text: "O titular também pode apresentar reclamação diretamente à <b>Autoridade Nacional de Proteção de Dados (ANPD)</b>, por meio dos canais oficiais disponíveis em www.gov.br/anpd." },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // 3. POLÍTICA DE ASSINATURAS E PAGAMENTOS (v2)
  // ══════════════════════════════════════════════════════════
  subscriptions: {
    title: "Política de Assinaturas e Pagamentos",
    version: "Versão 1.0 — Vigência a partir de 30 de março de 2025",
    sections: [
      { type: "h1", text: "Introdução" },
      { type: "body", text: 'Esta Política de Assinaturas e Pagamentos descreve as regras aplicáveis aos planos disponíveis na Plataforma <b>Litterae</b>, aos métodos de pagamento aceitos, aos ciclos de cobrança, ao processo de upgrade e cancelamento de planos, ao uso de cupons de desconto e ao tratamento de falhas de pagamento.' },
      { type: "body", text: 'Esta Política integra os <a data-doc="terms">Termos de Uso</a> do Litterae e deve ser lida em conjunto com os demais documentos do ecossistema jurídico da Plataforma. Ao contratar qualquer plano pago, o Usuário declara ter lido e concordado com as condições aqui estabelecidas.' },

      { type: "h1", text: "1. Planos Disponíveis" },

      { type: "h2", text: "1.1 Plano Gratuito (Free)" },
      { type: "body", text: "O plano gratuito é disponibilizado sem custo ao Usuário e não possui data de expiração. Oferece acesso integral à Plataforma, porém com cotas de uso reduzidas, conforme definido pelo Litterae e sujeito a alterações a qualquer momento, mediante comunicação prévia." },
      { type: "body", text: "O plano gratuito é automaticamente atribuído ao Usuário nas seguintes situações: no momento do cadastro; após o encerramento ou expiração de um plano pago sem renovação; e em caso de falha de pagamento não resolvida dentro do prazo estabelecido." },

      { type: "h2", text: "1.2 Planos Pagos" },
      { type: "body", text: "Os planos pagos conferem acesso a funcionalidades ampliadas da Plataforma. Os planos, seus preços e descrições estão disponíveis na página de planos da Plataforma e podem ser atualizados pelo Litterae, com aviso prévio de 30 (trinta) dias aos Usuários já assinantes." },
      { type: "item", text: "<b>Mensal:</b> cobrança realizada a cada 30 (trinta) dias, renovada automaticamente no método de pagamento recorrente ou mediante novo pagamento avulso." },
      { type: "item", text: "<b>Anual:</b> cobrança realizada a cada 12 (doze) meses, renovada automaticamente no método de pagamento recorrente ou mediante novo pagamento avulso." },
      { type: "body", text: "Cada Usuário pode manter apenas uma assinatura ativa por vez. A contratação de um novo plano enquanto outro estiver ativo aciona automaticamente o fluxo de upgrade descrito na Seção 5 desta Política." },

      { type: "h1", text: "2. Métodos de Pagamento" },
      { type: "body", text: "O Litterae aceita os seguintes métodos de pagamento, processados pelo <b>Mercado Pago</b>:" },

      { type: "h2", text: "2.1 PIX" },
      { type: "body", text: "Pagamento avulso, não recorrente, realizado via chave PIX. Ao optar por este método, o Usuário é direcionado ao checkout do Mercado Pago, onde gera o código PIX e realiza o pagamento. A ativação do plano ocorre após a confirmação do pagamento pelo Mercado Pago, via notificação de webhook." },
      { type: "body", text: "O pagamento via PIX não gera cobrança automática na renovação. O Usuário deverá realizar novo pagamento a cada ciclo para manter o acesso ao plano pago." },

      { type: "h2", text: "2.2 Cartão de Crédito — Pagamento Avulso" },
      { type: "body", text: "Pagamento único realizado com cartão de crédito, sem vínculo de recorrência. O valor é cobrado integralmente no momento da contratação. Assim como no PIX, o Usuário deverá realizar novo pagamento a cada ciclo para manter o acesso." },
      { type: "body", text: "Os dados do cartão são tokenizados diretamente pelo Mercado Pago por meio do componente Brick, e nunca trafegam ou são armazenados nos servidores do Litterae." },

      { type: "h2", text: "2.3 Cartão de Crédito — Assinatura Recorrente" },
      { type: "body", text: "Modalidade de cobrança automática vinculada a um preapproval (pré-autorização) criado junto ao Mercado Pago. O Usuário autoriza o Litterae a realizar cobranças periódicas no cartão de crédito informado, de acordo com o ciclo do plano contratado (mensal ou anual)." },
      { type: "body", text: "A primeira cobrança ocorre no momento da contratação. As cobranças subsequentes são realizadas automaticamente pelo Mercado Pago na data de renovação do ciclo, sem necessidade de nova ação do Usuário." },
      { type: "body", text: "O Usuário pode cancelar a recorrência a qualquer momento, conforme descrito na Seção 6 desta Política. O cancelamento interrompe as cobranças futuras e, em regra, não implica reembolso do ciclo já pago, ressalvado o direito de arrependimento exercido em até 7 (sete) dias corridos da contratação, bem como outras hipóteses previstas nesta Política ou na legislação aplicável." },

      { type: "h1", text: "3. Ativação e Vigência do Plano" },
      { type: "body", text: "O acesso ao plano pago é ativado após a confirmação do pagamento pelo Mercado Pago, comunicada ao Litterae por meio de notificação de webhook. A ativação por webhook pode ocorrer com alguns minutos de defasagem em relação ao momento do pagamento." },
      { type: "body", text: "Na modalidade de assinatura recorrente com cartão de crédito, quando o Mercado Pago retorna o status <i>authorized</i> imediatamente após a criação do preapproval, o acesso ao plano é liberado de forma antecipada, sem aguardar o webhook do primeiro pagamento." },
      { type: "body", text: "A vigência do plano pago inicia-se na data de ativação e encerra-se ao final do ciclo contratado (mensal ou anual), salvo renovação. Após o encerramento sem renovação, o Usuário é automaticamente rebaixado para o plano gratuito." },

      { type: "h1", text: "4. Renovação e Expiração" },
      { type: "body", text: "Planos pagos na modalidade recorrente são renovados automaticamente ao final de cada ciclo, mediante cobrança no cartão de crédito vinculado ao preapproval." },
      { type: "body", text: "Planos pagos via PIX ou cartão avulso não possuem renovação automática. O Usuário deverá iniciar um novo pagamento antes do encerramento do ciclo para manter o acesso sem interrupção." },
      { type: "body", text: "Caso o plano expire sem renovação, o sistema rebaixa automaticamente o Usuário para o plano gratuito, sem prejuízo do histórico de conteúdos e configurações da Conta." },

      { type: "h1", text: "5. Upgrade de Plano e Crédito Proporcional" },
      { type: "body", text: "O Usuário pode realizar o upgrade para um plano de maior valor a qualquer momento, inclusive durante um ciclo vigente. O Litterae aplica automaticamente um <b>crédito proporcional</b> correspondente aos dias restantes do plano atual, abatendo esse valor do preço do novo plano." },
      { type: "body", text: "O crédito proporcional é calculado com base na taxa diária do plano atual (valor do plano dividido pelo número de dias do ciclo) multiplicada pelo número de dias restantes até o término do ciclo vigente." },
      { type: "body", text: "Quando o crédito proporcional for igual ou superior ao valor do novo plano, o upgrade é realizado de forma gratuita e imediata, sem necessidade de novo pagamento." },
      { type: "body", text: "Na modalidade recorrente, o preapproval anterior é cancelado após a confirmação do upgrade, e um novo preapproval é criado para o plano atualizado. Os ciclos subsequentes serão cobrados pelo valor integral do novo plano." },
      { type: "body", text: "O downgrade para planos de menor valor não gera reembolso proporcional, exceto nas hipóteses expressamente previstas nesta Política ou por determinação legal." },

      { type: "h1", text: "6. Cancelamento" },
      { type: "body", text: "O Usuário pode cancelar sua assinatura paga a qualquer momento, diretamente pela Plataforma em <b>Perfil &gt; Resumo da Conta</b>, ou pelos canais de suporte." },
      { type: "body", text: "O cancelamento de uma assinatura recorrente interrompe as cobranças futuras junto ao Mercado Pago. O acesso ao plano pago é mantido até o fim do ciclo já pago, após o qual o Usuário é automaticamente rebaixado para o plano gratuito." },
      { type: "body", text: "Quando a contratação de plano pago ocorrer pela internet, o Usuário poderá exercer o direito de arrependimento em até 7 (sete) dias corridos da contratação, nos termos do art. 49 do Código de Defesa do Consumidor, com cancelamento da assinatura e reembolso dos valores pagos no período." },

      { type: "h1", text: "7. Falhas de Pagamento e Renovação" },
      { type: "body", text: "Em caso de falha na cobrança automática de uma assinatura recorrente, o Mercado Pago pode realizar novas tentativas de cobrança conforme sua política interna. O Litterae é notificado do resultado de cada tentativa por meio de webhook." },
      { type: "body", text: "Confirmada a falha definitiva, o Litterae aplica a seguinte lógica de fallback, nesta ordem:" },
      { type: "item", text: "<b>1. Reativação de plano pago anterior vigente:</b> se o Usuário possuir outra assinatura paga com data de término futura, ela é reativada automaticamente." },
      { type: "item", text: "<b>2. Reativação de qualquer plano anterior vigente:</b> caso não haja plano pago anterior, qualquer outra assinatura vigente (incluindo gratuita) é reativada." },
      { type: "item", text: "<b>3. Atribuição do plano gratuito:</b> na ausência de qualquer assinatura anterior vigente, o Usuário é automaticamente movido para o plano gratuito." },
      { type: "body", text: "O Usuário é notificado sobre a falha de pagamento e o consequente rebaixamento de plano por meio de mensagem exibida na Plataforma no próximo acesso." },

      { type: "h1", text: "8. Cupons de Desconto" },

      { type: "h2", text: "8.1 Tipos de cupom por elegibilidade" },
      { type: "item", text: "<b>ANYONE:</b> disponível para qualquer Usuário, sem restrição." },
      { type: "item", text: "<b>TARGETED:</b> vinculado a Usuários específicos previamente cadastrados pelo Litterae. Somente o Usuário para o qual o cupom foi direcionado pode utilizá-lo." },
      { type: "item", text: "<b>FIRST_PAID:</b> válido exclusivamente para Usuários que nunca contrataram um plano pago aprovado na Plataforma." },

      { type: "h2", text: "8.2 Aplicação do desconto" },
      { type: "body", text: "O desconto é calculado como um percentual sobre o valor base do plano. O valor final cobrado é o resultado da subtração do desconto sobre o preço integral." },
      { type: "body", text: "Na modalidade recorrente, o cupom pode ser aplicado: (i) apenas no primeiro ciclo de cobrança; ou (ii) em todos os ciclos, conforme a configuração definida pelo Litterae para aquele cupom específico." },

      { type: "h2", text: "8.3 Restrições de uso" },
      { type: "body", text: "Cada cupom pode ser utilizado apenas uma vez por Usuário. Cupons possuem data de validade e, quando esgotados (número máximo de resgates atingido), não podem ser aplicados por novos Usuários, ainda que válidos." },
      { type: "body", text: "Em caso de pagamento rejeitado ou cancelado após a aplicação de um cupom, a reserva do cupom é cancelada e a capacidade é liberada para outros Usuários, não sendo computado o uso pelo Usuário que não concluiu o pagamento." },

      { type: "h1", text: "9. Nota Fiscal de Serviço (NFS-e)" },
      { type: "body", text: "O Litterae emite Nota Fiscal de Serviço Eletrônico (NFS-e) para cada pagamento aprovado de plano pago. A nota fiscal é emitida automaticamente após a confirmação do pagamento e enviada para o e-mail informado pelo Usuário no momento do pagamento." },

      { type: "h1", text: "10. Alterações de Preço" },
      { type: "body", text: "O Litterae reserva-se o direito de alterar os preços dos planos a qualquer momento, mediante notificação ao Usuário com antecedência mínima de 30 (trinta) dias. Para Usuários com assinatura recorrente ativa, a alteração de preço somente entrará em vigor a partir do próximo ciclo de renovação após o prazo de notificação." },
      { type: "body", text: "A continuidade do uso da Plataforma após a entrada em vigor dos novos valores implica a aceitação das condições alteradas. Caso o Usuário não concorde, poderá cancelar sua assinatura antes da data de vigência sem penalidade." },

      { type: "h1", text: "11. Disposições Gerais" },
      { type: "body", text: "O processamento de todos os pagamentos é realizado pelo <b>Mercado Pago</b>, sujeito aos seus próprios termos de serviço e políticas. O Litterae não se responsabiliza por falhas, atrasos ou recusas de pagamento originadas na infraestrutura do Mercado Pago." },
      { type: "body", text: "Todas as transações são realizadas em <b>Reais (BRL)</b>. O Litterae não opera em moedas estrangeiras." },
      { type: "body", text: "Em caso de dúvidas sobre cobranças, o Usuário pode acionar o suporte diretamente pela Plataforma, através do botão <b>Fale Conosco &gt; Suporte</b>." },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // 4. POLÍTICA DE USO ACEITÁVEL (v2)
  // ══════════════════════════════════════════════════════════
  acceptable: {
    title: "Política de Uso Aceitável",
    version: "Versão 1.0 — Vigência a partir de 30 de março de 2025",
    sections: [
      { type: "h1", text: "Introdução" },
      { type: "body", text: "A presente Política de Uso Aceitável estabelece os comportamentos permitidos e proibidos no uso da Plataforma <b>Litterae</b>, com o objetivo de garantir um ambiente seguro, respeitoso e alinhado à natureza literária e cultural do ecossistema." },
      { type: "body", text: 'Esta Política integra os <a data-doc="terms">Termos de Uso</a> do Litterae e aplica-se a todos os Usuários, independentemente do plano contratado. O descumprimento de qualquer disposição aqui prevista pode resultar em suspensão temporária ou encerramento permanente da Conta, sem prejuízo de outras medidas cabíveis.' },

      { type: "h1", text: "1. Uso Permitido" },
      { type: "body", text: "O Usuário está autorizado a utilizar a Plataforma para as seguintes finalidades:" },
      { type: "body", text: 'Acessar, ler e interagir com os conteúdos literários disponibilizados conforme o plano contratado; gerenciar sua assinatura, dados de perfil e preferências de conta; publicar resenhas, anotações e comentários próprios sobre obras literárias, desde que em conformidade com esta Política; compartilhar conteúdos da Plataforma nos limites expressamente autorizados pela <a data-doc="copyright">Política de Direitos Autorais</a>; e utilizar as funcionalidades interativas da Plataforma para fins pessoais, educacionais ou profissionais ligados ao universo literário.' },

      { type: "h1", text: "2. Conduta Esperada" },
      { type: "body", text: "O Litterae é uma comunidade orientada ao universo literário. Ao interagir com outros Usuários ou publicar qualquer conteúdo, espera-se do Usuário:" },
      { type: "body", text: "Respeito e civilidade nas interações, sem ataques pessoais, linguagem ofensiva ou assédio de qualquer natureza; honestidade intelectual na produção de resenhas e opiniões, sem atribuição falsa de autoria ou manipulação de avaliações; uso responsável das funcionalidades colaborativas, sem prejudicar a experiência de outros Usuários; e cumprimento das leis brasileiras aplicáveis, incluindo as relativas a direitos autorais, privacidade e proteção de dados." },

      { type: "h1", text: "3. Usos Proibidos" },
      { type: "body", text: "É expressamente vedado ao Usuário, entre outros comportamentos incompatíveis com o espírito da Plataforma:" },

      { type: "h2", text: "3.1 Violações de segurança e integridade" },
      { type: "body", text: "Tentar acessar Contas de outros Usuários sem autorização; realizar ataques de força bruta, injeção de código, cross-site scripting (XSS) ou qualquer outra técnica de exploração de vulnerabilidades; interferir no funcionamento da Plataforma por meio de sobrecarga, bots, scrapers ou automações não autorizadas; e contornar mecanismos de autenticação, controle de acesso ou limitações de uso da Plataforma." },

      { type: "h2", text: "3.2 Conteúdo proibido" },
      { type: "body", text: "Publicar conteúdo que seja difamatório, obsceno, ameaçador, discriminatório ou que incite ódio com base em raça, etnia, gênero, orientação sexual, religião, nacionalidade ou qualquer outra característica protegida; divulgar informações falsas, enganosas ou que possam induzir outros Usuários a erro; publicar conteúdo que viole direitos autorais de terceiros, incluindo reprodução não autorizada de obras literárias integrais ou substanciais; e publicar spam, conteúdo publicitário não solicitado ou links para sites externos com fins comerciais." },

      { type: "h2", text: "3.3 Abuso da plataforma" },
      { type: "body", text: "Criar múltiplas Contas para contornar restrições, suspensões ou limites de uso; utilizar a Plataforma para coletar dados de outros Usuários sem consentimento; compartilhar credenciais de acesso com terceiros ou permitir que outros utilizem sua Conta; e usar a Plataforma para fins ilegais ou para causar dano financeiro ao Litterae ou a terceiros." },

      { type: "h2", text: "3.4 Abuso do sistema de pagamentos" },
      { type: "body", text: 'Utilizar dados de pagamento de terceiros sem autorização; realizar chargebacks fraudulentos ou contestações indevidas junto ao Mercado Pago ou a instituições financeiras; e tentar explorar cupons de desconto em desacordo com as regras estabelecidas na <a data-doc="subscriptions">Política de Assinaturas e Pagamentos</a>, incluindo criação de Contas falsas para obtenção indevida de benefícios.' },

      { type: "h1", text: "4. Conteúdo de Terceiros e Links Externos" },
      { type: "body", text: "A Plataforma pode conter links ou referências a conteúdos de terceiros. O Litterae não controla, endossa nem se responsabiliza por conteúdos externos, incluindo sua precisão, legalidade ou adequação. O acesso a conteúdos externos é de inteira responsabilidade do Usuário." },

      { type: "h1", text: "5. Moderação e Remoção de Conteúdo" },
      { type: "body", text: "O Litterae reserva-se o direito de monitorar, moderar, editar ou remover qualquer conteúdo publicado na Plataforma que viole esta Política, os Termos de Uso ou a legislação aplicável, sem aviso prévio ao Usuário." },
      { type: "body", text: "Usuários que identificarem conteúdo potencialmente violador podem reportá-lo diretamente pela Plataforma, através do botão <b>Fale Conosco &gt; Suporte</b>. O Litterae analisará as denúncias e tomará as medidas cabíveis no menor prazo possível." },
      { type: "body", text: "A remoção de conteúdo por violação desta Política não gera direito a reembolso de valores pagos nem indenização de qualquer natureza ao Usuário." },

      { type: "h1", text: "6. Consequências do Descumprimento" },
      { type: "body", text: "O descumprimento desta Política pode acarretar, a exclusivo critério do Litterae e de acordo com a gravidade da violação:" },
      { type: "item", text: "<b>Advertência:</b> notificação ao Usuário sobre a violação identificada, com orientação para adequação do comportamento." },
      { type: "item", text: "<b>Suspensão temporária:</b> bloqueio do acesso à Conta por período determinado, sem prejuízo da cobrança da assinatura vigente." },
      { type: "item", text: "<b>Encerramento permanente:</b> exclusão da Conta e cancelamento imediato da assinatura, sem reembolso dos valores pagos, nos casos de violações graves ou reincidentes." },
      { type: "item", text: "<b>Medidas legais:</b> nas hipóteses de dano ao Litterae, a outros Usuários ou a terceiros, o Litterae poderá adotar as medidas judiciais e extrajudiciais cabíveis, incluindo o acionamento das autoridades competentes." },

      { type: "h1", text: "7. Relato de Abusos" },
      { type: "body", text: "O Usuário que tomar conhecimento de qualquer uso indevido da Plataforma, violação desta Política ou atividade suspeita é encorajado a reportar imediatamente ao Litterae através do botão <b>Fale Conosco &gt; Suporte</b>, disponível na Plataforma." },
      { type: "body", text: "O Litterae trata todos os relatos com seriedade e confidencialidade, comprometendo-se a analisar cada caso e a comunicar ao Usuário denunciante, quando possível, as providências adotadas." },

      { type: "h1", text: "8. Atualizações desta Política" },
      { type: "body", text: "O Litterae pode atualizar esta Política a qualquer momento para refletir mudanças na Plataforma, na legislação aplicável ou nas práticas da comunidade. Alterações relevantes serão comunicadas com antecedência mínima de 15 (quinze) dias, por e-mail ou notificação na Plataforma." },
    ],
  },

  // ══════════════════════════════════════════════════════════
  // 5. POLÍTICA DE DIREITOS AUTORAIS (v2)
  // ══════════════════════════════════════════════════════════
  copyright: {
    title: "Política de Direitos Autorais",
    version: "Versão 1.0 — Vigência a partir de 30 de março de 2025",
    sections: [
      { type: "h1", text: "Introdução" },
      { type: "body", text: "A presente Política de Direitos Autorais disciplina as regras relativas à propriedade intelectual dos conteúdos disponibilizados na Plataforma <b>Litterae</b>, os direitos e obrigações dos Usuários quanto ao uso desses conteúdos, e os procedimentos para notificação de suposta violação de direitos autorais." },
      { type: "body", text: "O Litterae tem profundo respeito pela criação intelectual e pela cultura literária. Esta Política reflete o compromisso da Plataforma com a proteção dos autores, editores e demais titulares de direitos, em conformidade com a <b>Lei de Direitos Autorais brasileira (Lei nº 9.610/1998)</b> e demais normas aplicáveis." },
      { type: "body", text: 'Esta Política integra os <a data-doc="terms">Termos de Uso</a> do Litterae e deve ser lida em conjunto com a <a data-doc="acceptable">Política de Uso Aceitável</a>.' },

      { type: "h1", text: "1. Titularidade dos Conteúdos da Plataforma" },

      { type: "h2", text: "1.1 Conteúdos do Litterae" },
      { type: "body", text: "Todos os elementos originais desenvolvidos pelo Litterae — incluindo o código-fonte da Plataforma, design de interface, logotipos, marcas, textos institucionais, estrutura de navegação e demais componentes criativos — são de titularidade exclusiva do Litterae ou de seus licenciantes, protegidos pela legislação de propriedade intelectual vigente." },
      { type: "body", text: "É vedada qualquer reprodução, distribuição, modificação ou uso comercial desses elementos sem autorização prévia e expressa do Litterae." },

      { type: "h2", text: "1.2 Conteúdos literários de terceiros" },
      { type: "body", text: "Os conteúdos literários disponibilizados na Plataforma — obras, excertos, sinopses, capas e demais materiais de autores e editoras — pertencem a seus respectivos titulares de direitos. O Litterae os disponibiliza mediante licenciamento, acordo contratual ou nos limites do domínio público, conforme aplicável a cada obra." },
      { type: "body", text: "O acesso do Usuário a esses conteúdos é estritamente pessoal e não transfere qualquer direito de propriedade, reprodução ou distribuição sobre as obras." },

      { type: "h2", text: "1.3 Conteúdos publicados pelos Usuários" },
      { type: "body", text: "Resenhas, anotações, comentários e demais conteúdos originais publicados pelo Usuário na Plataforma permanecem de titularidade do próprio Usuário. Ao publicá-los, o Usuário concede ao Litterae uma licença não exclusiva, gratuita, mundial e pelo prazo de vigência da Conta, para hospedar, exibir e distribuir tais conteúdos exclusivamente no âmbito da Plataforma." },
      { type: "body", text: "O Usuário declara e garante que é o legítimo titular ou licenciado de todo conteúdo que publicar, e que tal publicação não infringe direitos de terceiros." },

      { type: "h1", text: "2. Direitos Concedidos ao Usuário" },
      { type: "body", text: "O acesso à Plataforma concede ao Usuário uma licença limitada, pessoal, intransferível e não exclusiva para:" },
      { type: "body", text: "Visualizar e ler os conteúdos literários disponíveis conforme o plano contratado; realizar anotações e marcações pessoais nas ferramentas oferecidas pela Plataforma, podendo defini-las como privadas, visíveis a usuários ou grupos específicos, ou públicas, conforme as opções disponíveis; e compartilhar trechos de obras nos limites expressamente permitidos pela Plataforma, respeitando as disposições da Lei nº 9.610/1998 quanto ao direito de citação." },
      { type: "body", text: "O direito de citação, nos termos da lei, permite a reprodução de pequenos trechos de obras protegidas para fins de crítica, comentário, ensino ou pesquisa, desde que acompanhados da identificação da obra e do autor, e sem fins comerciais." },

      { type: "h1", text: "3. Restrições de Uso dos Conteúdos" },
      { type: "body", text: 'É expressamente proibido ao Usuário, sem prejuízo de outras vedações previstas nos <a data-doc="terms">Termos de Uso</a> e na <a data-doc="acceptable">Política de Uso Aceitável</a>:' },
      { type: "body", text: "Reproduzir, copiar, distribuir, vender ou sublicenciar obras literárias integrais ou substanciais disponibilizadas na Plataforma, em qualquer formato ou meio; baixar, exportar ou extrair conteúdos por meios automatizados, incluindo scrapers, bots ou qualquer técnica de raspagem de dados; remover, alterar ou ocultar avisos de direitos autorais, créditos de autoria ou indicações de origem das obras; utilizar os conteúdos da Plataforma para treinar modelos de inteligência artificial ou para qualquer outro uso que não seja a leitura e fruição pessoal; e compartilhar credenciais de acesso para que terceiros fruam de conteúdos pagos sem a devida assinatura." },

      { type: "h1", text: "4. Obras em Domínio Público" },
      { type: "body", text: "O Litterae pode disponibilizar obras em domínio público, assim consideradas aquelas cujos direitos patrimoniais expiraram nos termos da legislação aplicável — no Brasil, 70 (setenta) anos após a morte do autor, contados a partir de 1º de janeiro do ano subsequente ao do falecimento." },
      { type: "body", text: "Ainda que uma obra esteja em domínio público, os direitos morais do autor — como o direito de paternidade e de integridade da obra — são perpétuos e inalienáveis, devendo ser respeitados pelo Usuário em qualquer uso ou citação." },
      { type: "body", text: "Além disso, eventuais edições críticas, traduções, prefácios e outros elementos editoriais acrescidos a obras em domínio público podem estar protegidos por direitos autorais próprios de seus respectivos autores ou editoras." },

      { type: "h1", text: "5. Uso de Inteligência Artificial e Conteúdo Gerado" },
      { type: "body", text: "É vedado ao Usuário utilizar os conteúdos acessados na Plataforma como insumo para o treinamento, ajuste fino ou alimentação de modelos de inteligência artificial, sistemas de aprendizado de máquina ou bases de dados destinadas a tais finalidades, sem autorização prévia e expressa do Litterae e dos titulares dos direitos sobre as obras." },
      { type: "body", text: "Conteúdos gerados por ferramentas de inteligência artificial e publicados pelo Usuário na Plataforma são de responsabilidade exclusiva do Usuário, que declara ter os direitos necessários para sua publicação e que o conteúdo não infringe direitos de terceiros." },

      { type: "h1", text: "6. Notificação de Suposta Violação de Direitos Autorais" },
      { type: "body", text: "Se você é titular de direitos autorais ou seu representante legal e acredita que algum conteúdo disponibilizado na Plataforma viola seus direitos, encaminhe uma notificação ao Litterae através do botão <b>Fale Conosco &gt; Suporte</b>, contendo as seguintes informações:" },
      { type: "item", text: "Identificação da obra ou conteúdo supostamente infringido, com descrição suficiente para sua localização na Plataforma;" },
      { type: "item", text: "Identificação do conteúdo que você alega ser infringing, com URL ou localização específica;" },
      { type: "item", text: "Seus dados de contato (nome, e-mail e, se aplicável, endereço);" },
      { type: "item", text: "Declaração de que você é o titular dos direitos ou está autorizado a agir em nome do titular;" },
      { type: "item", text: "Declaração de boa-fé de que o uso do conteúdo não foi autorizado pelo titular, por seu agente ou pela lei." },
      { type: "body", text: "O Litterae analisará a notificação e, verificada a plausibilidade da violação, tomará as medidas cabíveis, que podem incluir a remoção ou o bloqueio do conteúdo apontado, com notificação ao Usuário que o publicou." },
      { type: "body", text: "Notificações de má-fé ou com informações falsas poderão responsabilizar o notificante pelos danos causados ao Usuário afetado e ao Litterae." },

      { type: "h1", text: "7. Contranotificação" },
      { type: "body", text: "O Usuário cujo conteúdo tenha sido removido em razão de notificação de violação pode apresentar contranotificação ao Litterae, também através do botão <b>Fale Conosco &gt; Suporte</b>, demonstrando que: o conteúdo foi removido por engano ou identificação incorreta; possui autorização do titular para o uso em questão; ou o uso se enquadra nas exceções e limitações previstas na Lei nº 9.610/1998, como o direito de citação." },
      { type: "body", text: "O Litterae avaliará a contranotificação e poderá restaurar o conteúdo removido, caso a argumentação apresentada seja procedente." },

      { type: "h1", text: "8. Atualizações desta Política" },
      { type: "body", text: "O Litterae pode atualizar esta Política a qualquer momento para refletir mudanças na legislação, no modelo de negócios ou nas práticas de licenciamento de conteúdo. Alterações relevantes serão comunicadas com antecedência mínima de 15 (quinze) dias, por e-mail ou notificação na Plataforma." },
    ],
  },
};

const currentDoc = computed(() => documents[activeDoc.value]);

function handleDocLink(event) {
  const anchor = event.target.closest("a[data-doc]");
  if (!anchor) return;
  event.preventDefault();
  const docId = anchor.getAttribute("data-doc");
  if (docId) navigateTo(docId);
}
</script>

<template>
  <div class="legal-overlay">
    <div class="legal-panel">

      <!-- Cabeçalho -->
      <header class="legal-header">
        <div class="header-brand">
          <img src="/litterae_pena_fundo_transparente.png" alt="Logo Litterae" class="brand-logo" />
          <span class="brand-name">Litterae</span>
          <span class="header-sep">·</span>
          <span class="header-label">Documentos Legais</span>
        </div>
        <button @click="closeModal" class="close-btn" aria-label="Fechar">
          <i class="fas fa-times"></i>
        </button>
      </header>

      <div class="legal-body">

        <!-- Menu lateral -->
        <nav class="legal-sidebar">
          <p class="sidebar-label">Documentos</p>
          <ul>
            <li v-for="doc in docs" :key="doc.id">
              <button
                type="button"
                :class="['sidebar-btn', { active: activeDoc === doc.id }]"
                @click="navigateTo(doc.id)"
              >
                <i :class="doc.icon"></i>
                <span>{{ doc.label }}</span>
              </button>
            </li>
          </ul>
        </nav>

        <!-- Área de conteúdo -->
        <main id="legal-content-area" class="legal-content">

          <!-- Cabeçalho do documento -->
          <div class="doc-header">
            <div class="doc-badge">
              <i :class="docs.find(d => d.id === activeDoc)?.icon"></i>
            </div>
            <div>
              <h1 class="doc-title">{{ currentDoc.title }}</h1>
              <p class="doc-version">{{ currentDoc.version }}</p>
            </div>
          </div>

          <div class="doc-divider"></div>

          <!-- Seções do documento -->
          <div class="doc-body">
            <template v-for="(section, i) in currentDoc.sections" :key="i">

              <!-- H1 -->
              <h2 v-if="section.type === 'h1'" class="sec-h1">
                {{ section.text }}
              </h2>

              <!-- H2 -->
              <h3 v-else-if="section.type === 'h2'" class="sec-h2">
                {{ section.text }}
              </h3>

              <!-- Parágrafo -->
              <p
                v-else-if="section.type === 'body'"
                class="sec-body"
                v-html="section.text"
                @click="handleDocLink($event)"
              ></p>

              <!-- Item de lista -->
              <div
                v-else-if="section.type === 'item'"
                class="sec-item"
                v-html="section.text"
                @click="handleDocLink($event)"
              ></div>

            </template>
          </div>

        </main>
      </div>

    </div>
  </div>
</template>

<style scoped>
/* ─── Overlay ─────────────────────────────────────────────── */
.legal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.25);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 1rem;
  padding-top: 4vh;
  z-index: 1000;
  overflow-y: auto;
  font-family: "Poppins", sans-serif;
}

.legal-panel {
  width: 100%;
  max-width: 980px;
  height: min(92vh, 880px);
  background: var(--bg-content);
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ─── Header ──────────────────────────────────────────────── */
.legal-header {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 1.2rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-header);
}

.header-brand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: var(--text-dark);
}

.brand-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.brand-name {
  font-weight: 700;
  color: #1a1a1a;
}

.header-sep {
  color: var(--text-light);
}

.header-label {
  color: var(--text-light);
  font-size: 0.85rem;
}

.close-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.375rem;
  border-radius: 8px;
  color: var(--text-light);
  font-size: 1.1rem;
  line-height: 1;
  transition: background-color 0.2s, color 0.2s;
}
.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.08);
  color: var(--text-dark);
}

/* ─── Body ────────────────────────────────────────────────── */
.legal-body {
  flex: 1;
  display: flex;
  overflow: hidden;
}

/* ─── Sidebar ─────────────────────────────────────────────── */
.legal-sidebar {
  flex-shrink: 0;
  width: 220px;
  border-right: 1px solid var(--border-color);
  background: var(--bg-sidebar-left);
  padding: 1rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow-y: auto;
}

.sidebar-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-light);
  padding: 0 0.5rem;
  margin-bottom: 0.4rem;
}

.legal-sidebar ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.sidebar-btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 0.75rem;
  border: none;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--text-dark);
  text-align: left;
  transition: background-color 0.18s, color 0.18s;
  line-height: 1.3;
}

.sidebar-btn i {
  font-size: 0.85rem;
  flex-shrink: 0;
  color: var(--text-light);
  transition: color 0.18s;
}

.sidebar-btn:hover {
  background-color: #f7f7f7;
  color: #1a1a1a;
}
.sidebar-btn:hover i {
  color: #444444;
}

.sidebar-btn.active {
  background-color: var(--surface-hover);
  color: #1a1a1a;
  font-weight: 600;
}
.sidebar-btn.active i {
  color: #444444;
}

/* ─── Content ─────────────────────────────────────────────── */
.legal-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.75rem 2rem 2.5rem 2rem;
}

/* Cabeçalho do documento */
.doc-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.doc-badge {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1a1a1a, #444444);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.1rem;
}

.doc-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.2rem 0;
  line-height: 1.3;
}

.doc-version {
  font-size: 0.78rem;
  color: var(--text-light);
  margin: 0;
}

.doc-divider {
  height: 1px;
  background: linear-gradient(to right, #d0d0d0, transparent);
  margin-bottom: 1.5rem;
}

/* Corpo do documento */
.doc-body {
  max-width: 680px;
}

.sec-h1 {
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 1.6rem 0 0.5rem 0;
  padding-bottom: 0.3rem;
  border-bottom: 2px solid #d0d0d0;
}

.sec-h2 {
  font-size: 0.92rem;
  font-weight: 600;
  color: #444444;
  margin: 1.1rem 0 0.4rem 0;
}

.sec-body {
  font-size: 0.88rem;
  line-height: 1.7;
  color: #2C2C2C;
  margin: 0 0 0.65rem 0;
  text-align: justify;
}

.sec-item {
  font-size: 0.88rem;
  line-height: 1.7;
  color: #2C2C2C;
  margin: 0 0 0.5rem 0;
  padding-left: 1rem;
  border-left: 3px solid #d0d0d0;
  text-align: justify;
}

/* Links internos */
.doc-body :deep(a[data-doc]) {
  color: #444444;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  font-weight: 500;
  transition: color 0.15s;
}
.doc-body :deep(a[data-doc]:hover) {
  color: #a99df0;
}

/* ─── Responsivo ──────────────────────────────────────────── */
@media (max-width: 720px) {
  .legal-sidebar {
    width: 180px;
  }
  .legal-content {
    padding: 1.25rem 1.25rem 2rem 1.25rem;
  }
}

@media (max-width: 560px) {
  .legal-body {
    flex-direction: column;
  }
  .legal-sidebar {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid var(--border-color);
    padding: 0.6rem 0.75rem;
  }
  .legal-sidebar ul {
    flex-direction: row;
    flex-wrap: wrap;
  }
  .sidebar-btn {
    padding: 0.4rem 0.6rem;
    font-size: 0.78rem;
  }
  .sidebar-btn span {
    display: none;
  }
  .sidebar-btn i {
    font-size: 1rem;
  }
}
</style>

<style>
/* Links internos nos documentos legais — global para penetrar v-html */
.doc-body a[data-doc] {
  color: var(--link) !important;
  text-decoration: underline !important;
  text-underline-offset: 2px;
  cursor: pointer;
  font-weight: 500;
  transition: color 0.15s;
}
.doc-body a[data-doc]:hover {
  color: var(--link-hover) !important;
}
</style>